import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useCallback,
} from 'react';

import {
  isSingleCell,
  nextTabCell,
  nextVisibleCell,
  normalizeRange,
  singleRange,
  uid,
} from '../lib/table-ops';
import {
  cellEditText,
  cellSelection,
  cellText,
  tableCellRemoveElementAt,
  withCellText,
} from '../lib/cell-elements';
import {labelText} from '../lib/label-text';
import type {RichText} from '../lib/rich-text';
import type {TableCellData, TableConfig, TableConfigSnapshot} from '../lib/table-types';

// Cross-instance cell clipboard (module-scoped, survives React re-mounts).
let internalClipboard: {cells: TableCellData[][]} | null = null;

interface UseTableKeyboardParams {
  configRef: MutableRefObject<TableConfigSnapshot>;
  toCfg: (overrides: Partial<TableConfig>) => TableConfig;
  update: (cfg: TableConfig) => void;
  editRef: MutableRefObject<{cell: {row: number; col: number} | null; draft: RichText}>;
  setEditingCell: Dispatch<SetStateAction<{row: number; col: number} | null>>;
  setDraft: Dispatch<SetStateAction<RichText>>;
  tableWrapperRef: RefObject<HTMLDivElement | null>;
}

/**
 * Keyboard interaction for a focused table wrapper: arrow / Tab cell navigation,
 * Enter / printable-char to start editing, Escape to deselect, Delete/Backspace to
 * clear cells or remove a cell element, and Ctrl/Cmd+C / +V copy-paste (via a
 * module-scoped clipboard + the system clipboard). Extracted from TableElement.
 */
export function useTableKeyboard({
  configRef,
  toCfg,
  update,
  editRef,
  setEditingCell,
  setDraft,
  tableWrapperRef,
}: UseTableKeyboardParams) {
  const handleTableKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (editRef.current.cell) return; // textarea is handling keys
      const range = configRef.current.cellRange;
      if (!range) return;

      const n = normalizeRange(range);
      const ri = n.startRow;
      const ci = n.startCol;

      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          e.preventDefault();
          const next = nextVisibleCell(
            configRef.current.rows,
            ri,
            ci,
            e.key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight',
          );
          if (next) update(toCfg({cellRange: singleRange(next.row, next.col)}));
          break;
        }

        case 'Tab': {
          e.preventDefault();
          const next = nextTabCell(configRef.current.rows, ri, ci, e.shiftKey);
          if (next) update(toCfg({cellRange: singleRange(next.row, next.col)}));
          break;
        }

        case 'Enter': {
          e.preventDefault();
          const cell = configRef.current.rows[ri]?.cells[ci];
          if (cell && !cell.hidden) {
            setEditingCell({row: ri, col: ci});
            setDraft(cellEditText(cell, cell.element));
          }
          break;
        }

        case 'Escape': {
          e.preventDefault();
          update(toCfg({cellRange: null}));
          tableWrapperRef.current?.blur();
          break;
        }

        case 'Delete':
        case 'Backspace': {
          e.preventDefault();
          e.stopPropagation();
          // A single cell with elements + an active element → remove just that
          // element (not the cell, and never the whole table).
          if (isSingleCell(n)) {
            const {index, element} = cellSelection(configRef.current, ri, ci);
            if (element) {
              // The op carries the selection: shortening the stack is what
              // invalidates the index, so mending it is its job, not ours.
              update(tableCellRemoveElementAt(toCfg({}), ri, ci, index));
              break;
            }
          }
          // Otherwise clear cell content / legacy element across the range.
          const newRows = configRef.current.rows.map((row, rowIdx) => {
            if (rowIdx < n.startRow || rowIdx > n.endRow) return row;
            const cells = row.cells.map((cell, colIdx) => {
              if (colIdx < n.startCol || colIdx > n.endCol || cell.hidden) return cell;
              if (cell.element) return {...cell, element: undefined};
              return withCellText(cell, {text: ''});
            });
            return {...row, cells};
          });
          update(toCfg({rows: newRows}));
          break;
        }

        case 'c':
        case 'C': {
          if (!(e.ctrlKey || e.metaKey)) break;
          e.preventDefault();
          const cells: TableCellData[][] = [];
          for (let r = n.startRow; r <= n.endRow; r++) {
            const rowCells: TableCellData[] = [];
            for (let c = n.startCol; c <= n.endCol; c++) {
              const cell = configRef.current.rows[r]?.cells[c];
              if (cell) rowCells.push({...cell, id: uid()});
            }
            cells.push(rowCells);
          }
          internalClipboard = {cells};
          // Also push tab-separated plain text to the system clipboard.
          const text = cells.map((row) => row.map((c) => c.content).join('\t')).join('\n');
          navigator.clipboard.writeText(text).catch(() => {});
          break;
        }

        case 'v':
        case 'V': {
          if (!(e.ctrlKey || e.metaKey) || !internalClipboard) break;
          e.preventDefault();
          const cb = internalClipboard.cells;
          const newRows = configRef.current.rows.map((row, rowIdx) => {
            const cbRow = cb[rowIdx - n.startRow];
            if (!cbRow) return row;
            const cells = row.cells.map((cell, colIdx) => {
              const cbCell = cbRow[colIdx - n.startCol];
              if (!cbCell) return cell;
              return {...withCellText(cell, cellText(cbCell)), style: cbCell.style};
            });
            return {...row, cells};
          });
          update(toCfg({rows: newRows}));
          break;
        }

        default: {
          // Typing a printable character on a single-cell selection starts editing.
          if (isSingleCell(n) && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const cell = configRef.current.rows[ri]?.cells[ci];
            if (cell && !cell.hidden) {
              e.preventDefault();
              setEditingCell({row: ri, col: ci});
              const {element} = cellSelection(configRef.current, ri, ci);
              setDraft(element ? labelText(element) : {text: e.key});
            }
          }
          break;
        }
      }
    },
    [update, toCfg, configRef, editRef, setEditingCell, setDraft, tableWrapperRef],
  );

  return handleTableKeyDown;
}
